import { Minion } from './minion.js';

/** First wave spawn clock (FID-MIN-01). */
export const WAVE_FIRST_SPAWN_SEC = 5;
/** Subsequent wave interval (FID-MIN-01). */
export const WAVE_SPAWN_INTERVAL_SEC = 30;
/** Locked ARAM wave composition counts (FID-MIN-01). */
export const WAVE_COMPOSITION_LOCK = Object.freeze({
  melee: 3,
  caster: 3,
  cannonEvery: 3
});

/** Cannon wave clock lock after composition (FID-MIN-04). */
export const WAVE_CANNON_TIMING_LOCK = Object.freeze({
  everyWaves: WAVE_COMPOSITION_LOCK.cannonEvery,
  firstCannonWave: WAVE_COMPOSITION_LOCK.cannonEvery,
  /** gameTime when wave 3 is due: first @5s then +30s × 2 */
  firstCannonGameSec: WAVE_FIRST_SPAWN_SEC + WAVE_SPAWN_INTERVAL_SEC * 2
});

export class MinionSpawner {
  constructor({
    team = 'blue',
    spawnX = -12,
    spawnZ = -12,
    waypoints = [],
    mainTurretTarget = null,
    waveInterval = WAVE_SPAWN_INTERVAL_SEC,
    sequentialDelay = 0.8
  }) {
    this.team = team;
    this.spawnX = spawnX;
    this.spawnZ = spawnZ;
    this.waypoints = waypoints;
    this.mainTurretTarget = mainTurretTarget;
    this.waveInterval = waveInterval;
    this.sequentialDelay = sequentialDelay;
    this.spawnTimer = waveInterval;
    this.queueTimer = 0;
    this.spawnQueue = [];
    this.waveCount = 0;
    this.enemyInhibitorsDown = 0;
  }

  triggerWave() {
    this.waveCount++;
    const comp = getWaveComposition(this.waveCount, {
      enemyInhibitorsDown: this.enemyInhibitorsDown
    });
    enqueueByType(this, 'melee', comp.melee);
    enqueueByType(this, 'ranged', comp.caster);
    enqueueByType(this, 'cannon', comp.cannon);
    enqueueByType(this, 'super', comp.super);
  }

  spawnWave() {
    this.triggerWave();
    return [];
  }

  update(dt) {
    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0) {
      this.spawnTimer = this.waveInterval;
      this.triggerWave();
    }

    if (this.spawnQueue.length > 0) {
      this.queueTimer -= dt;
      if (this.queueTimer <= 0) {
        this.queueTimer = this.sequentialDelay;
        const item = this.spawnQueue.shift();

        const newMinion = new Minion({
          id: item.id,
          team: this.team,
          minionType: item.type,
          x: this.spawnX,
          z: this.spawnZ,
          waypoints: this.waypoints,
          mainTurretTarget: this.mainTurretTarget
        });

        return [newMinion];
      }
    }

    return null;
  }
}

function enqueueByType(spawner, type, count) {
  for (let i = 0; i < count; i++) {
    const letter = type[0];
    spawner.spawnQueue.push({
      id: `minion-${spawner.team}-${letter}-${spawner.waveCount}-${i}`,
      type
    });
  }
}

export function shouldSpawnWave(gameTime, lastSpawnTime) {
  if (lastSpawnTime === 0 && gameTime >= WAVE_FIRST_SPAWN_SEC) return true;
  return (gameTime - lastSpawnTime) >= WAVE_SPAWN_INTERVAL_SEC;
}

export function getWaveComposition(waveNumber, { enemyInhibitorsDown = 0 } = {}) {
  const isCannon = waveNumber % WAVE_CANNON_TIMING_LOCK.everyWaves === 0;
  const superCount = enemyInhibitorsDown >= 3 ? 2 : (enemyInhibitorsDown > 0 ? 1 : 0);
  return {
    melee: WAVE_COMPOSITION_LOCK.melee,
    caster: WAVE_COMPOSITION_LOCK.caster,
    cannon: isCannon && superCount === 0 ? 1 : 0,
    super: superCount
  };
}

export function countDeadEnemyInhibitors(structures, attackingTeam) {
  const enemyTeam = attackingTeam === 'blue' ? 'red' : 'blue';
  return (structures || []).filter(
    (s) => s && s.type === 'inhibitor' && s.team === enemyTeam && s.isDead
  ).length;
}
